import { z } from "zod"

import {
  correlationIdSchema,
  platformContextIdSchema,
  platformOperatorIdSchema,
  platformSessionIdSchema,
  tenantIdSchema,
} from "./identifiers.js"
import { tenantLifecycleStateV1Schema } from "./tenant-lifecycle-v1.js"

const timestampSchema = z.string().datetime({ offset: true })
const boundedReferenceSchema = z.string().regex(/^[a-z][a-z0-9_-]{1,47}$/)
const unique = <T>(values: readonly T[]): boolean =>
  new Set(values).size === values.length

export const tenantHealthStateV1Schema = z.enum([
  "healthy",
  "degraded",
  "unavailable",
  "unknown",
])
export const platformSubscriptionStateV1Schema = z.enum([
  "trialing",
  "active",
  "past_due",
  "restricted",
  "canceled",
  "unknown",
])
export const providerReadinessStateV1Schema = z.enum([
  "ready",
  "action_required",
  "degraded",
  "unknown",
])
export const tenantActionBlockerV1Schema = z.enum([
  "lifecycle_approval_required",
  "provisioning_incomplete",
  "route_not_ready",
  "health_degraded",
  "subscription_ambiguous",
  "entitlement_inactive",
  "provider_action_required",
  "export_incomplete",
  "retention_active",
  "legal_hold_active",
  "backup_incomplete",
  "version_conflict",
])

export const platformQueryEnvelopeV1Schema = z
  .object({
    contractVersion: z.literal(1),
    platformContextId: platformContextIdSchema,
    platformOperatorId: platformOperatorIdSchema,
    platformSessionId: platformSessionIdSchema,
    correlationId: correlationIdSchema,
    queryType: z.literal("TenantDirectory"),
    queryVersion: z.literal(1),
  })
  .strict()

export const tenantDirectoryFiltersV1Schema = z
  .object({
    exactTenantId: tenantIdSchema.nullable(),
    lifecycleStates: z.array(tenantLifecycleStateV1Schema).max(7),
    serviceTierRefs: z.array(boundedReferenceSchema).max(20),
    regionRefs: z.array(boundedReferenceSchema).max(20),
    cohortRefs: z.array(boundedReferenceSchema).max(20),
    minimumVersion: z.number().int().nonnegative().safe().nullable(),
    healthStates: z.array(tenantHealthStateV1Schema).max(4),
    subscriptionStates: z.array(platformSubscriptionStateV1Schema).max(6),
    providerReadinessStates: z.array(providerReadinessStateV1Schema).max(4),
    actionBlockers: z.array(tenantActionBlockerV1Schema).max(12),
  })
  .strict()
  .refine((filters) => [
    filters.lifecycleStates,
    filters.serviceTierRefs,
    filters.regionRefs,
    filters.cohortRefs,
    filters.healthStates,
    filters.subscriptionStates,
    filters.providerReadinessStates,
    filters.actionBlockers,
  ].every(unique), {
    message: "Tenant directory filters must be unique.",
  })

export const tenantDirectoryQueryV1Schema = z
  .object({
    envelope: platformQueryEnvelopeV1Schema,
    filters: tenantDirectoryFiltersV1Schema,
    page: z
      .object({
        limit: z.number().int().min(1).max(100),
        cursor: z.string().regex(/^[A-Za-z0-9_-]{8,128}$/).nullable(),
      })
      .strict(),
  })
  .strict()

export const tenantProviderReadinessV1Schema = z
  .object({
    providerCode: boundedReferenceSchema,
    state: providerReadinessStateV1Schema,
    safeFailureCode: z.string().regex(/^[A-Z][A-Z0-9_]{2,63}$/).nullable(),
  })
  .strict()

export const tenantDirectoryItemV1Schema = z
  .object({
    targetTenantId: tenantIdSchema,
    safeDisplayLabel: z.string().min(1).max(120).nullable(),
    lifecycleState: tenantLifecycleStateV1Schema,
    lifecycleVersion: z.number().int().positive().safe(),
    serviceTierRef: boundedReferenceSchema.nullable(),
    regionRef: boundedReferenceSchema.nullable(),
    cohortRef: boundedReferenceSchema.nullable(),
    healthState: tenantHealthStateV1Schema,
    subscriptionState: platformSubscriptionStateV1Schema,
    providerReadiness: z.array(tenantProviderReadinessV1Schema).max(32),
    actionBlockers: z.array(tenantActionBlockerV1Schema).max(12),
    observedAt: timestampSchema,
    stale: z.boolean(),
  })
  .strict()
  .superRefine((item, context) => {
    if (!unique(item.providerReadiness.map((entry) => entry.providerCode))) {
      context.addIssue({
        code: "custom",
        path: ["providerReadiness"],
        message: "Provider readiness entries must be unique.",
      })
    }
    if (!unique(item.actionBlockers)) {
      context.addIssue({
        code: "custom",
        path: ["actionBlockers"],
        message: "Tenant action blockers must be unique.",
      })
    }
  })

export const tenantDirectoryPageV1Schema = z
  .object({
    responseVersion: z.literal(1),
    correlationId: correlationIdSchema,
    items: z.array(tenantDirectoryItemV1Schema).max(100),
    nextCursor: z.string().regex(/^[A-Za-z0-9_-]{8,128}$/).nullable(),
    partial: z.boolean(),
    generatedAt: timestampSchema,
  })
  .strict()

export type PlatformQueryEnvelopeV1 = z.infer<
  typeof platformQueryEnvelopeV1Schema
>
export type TenantActionBlockerV1 = z.infer<
  typeof tenantActionBlockerV1Schema
>
export type TenantDirectoryFiltersV1 = z.infer<
  typeof tenantDirectoryFiltersV1Schema
>
export type TenantDirectoryItemV1 = z.infer<
  typeof tenantDirectoryItemV1Schema
>
export type TenantDirectoryPageV1 = z.infer<
  typeof tenantDirectoryPageV1Schema
>
export type TenantDirectoryQueryV1 = z.infer<
  typeof tenantDirectoryQueryV1Schema
>
export type TenantHealthStateV1 = z.infer<typeof tenantHealthStateV1Schema>
export type TenantProviderReadinessV1 = z.infer<
  typeof tenantProviderReadinessV1Schema
>
